import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { startOfDay, subDays } from 'date-fns';

export const useAuditOverviewStats = () => {
  return useQuery({
    queryKey: ['audit-overview-stats'],
    queryFn: async () => {
      const thirtyDaysAgo = startOfDay(subDays(new Date(), 30));
      const sevenDaysAgo = startOfDay(subDays(new Date(), 7));
      const today = startOfDay(new Date());

      // Fetch audit logs (last 30 days)
      const { data: auditLogs, error: auditError } = await supabase
        .from('audit_logs')
        .select('id, action, created_at')
        .gte('created_at', thirtyDaysAgo.toISOString());

      if (auditError) throw auditError;

      // Fetch billing automation logs (last 30 days)
      const { data: billingLogs, error: billingError } = await supabase
        .from('billing_automation_logs')
        .select('id, invoices_created, executed_at, errors')
        .gte('executed_at', thirtyDaysAgo.toISOString());

      if (billingError) throw billingError;

      // Count critical events (errors)
      const criticalEvents = (auditLogs?.filter(log => 
        log.action.includes('error') || log.action.includes('failed')
      ) || []).length + (billingLogs?.filter(log => 
        log.errors && Array.isArray(log.errors) && log.errors.length > 0
      ) || []).length;

      // Count invoices generated
      const invoicesGenerated = billingLogs?.reduce((sum, log) => 
        sum + (log.invoices_created || 0), 0
      ) || 0;

      // Count email events
      const emailsSent = auditLogs?.filter(log => 
        log.action === 'billing.email_sent'
      ).length || 0;

      const emailsFailed = auditLogs?.filter(log => 
        log.action === 'billing.email_failed'
      ).length || 0;

      // Calculate success rate
      const totalAutomations = billingLogs?.length || 0;
      const successfulAutomations = billingLogs?.filter(log => 
        !log.errors || !Array.isArray(log.errors) || log.errors.length === 0
      ).length || 0;
      const successRate = totalAutomations > 0 
        ? Math.round((successfulAutomations / totalAutomations) * 100) 
        : 100;

      // Activity by day (last 30 days)
      const activityByDay = Array.from({ length: 30 }, (_, i) => {
        const date = startOfDay(subDays(new Date(), 29 - i));
        const dateStr = date.toISOString().split('T')[0];
        
        const auditCount = auditLogs?.filter(log => 
          log.created_at.startsWith(dateStr)
        ).length || 0;
        
        const billingCount = billingLogs?.filter(log => 
          log.executed_at.startsWith(dateStr)
        ).length || 0;
        
        return {
          date: dateStr,
          count: auditCount + billingCount,
        };
      });

      // Distribution by type
      const adminEvents = auditLogs?.filter(log => 
        log.action.startsWith('company.') || 
        log.action.startsWith('user.') || 
        log.action.startsWith('plan.')
      ).length || 0;

      const billingEvents = auditLogs?.filter(log => 
        log.action.startsWith('billing.')
      ).length || 0;

      const systemEvents = auditLogs?.filter(log => 
        log.action.startsWith('permission.') || 
        log.action.startsWith('role.') || 
        log.action.startsWith('settings.')
      ).length || 0;

      return {
        totalEvents: (auditLogs?.length || 0) + (billingLogs?.length || 0),
        criticalEvents,
        invoicesGenerated,
        emailsSent,
        emailsFailed,
        successRate,
        activityByDay,
        distribution: [
          { name: 'Administração', value: adminEvents, color: 'hsl(var(--chart-1))' },
          { name: 'Faturamento', value: billingEvents + totalAutomations, color: 'hsl(var(--chart-2))' },
          { name: 'Sistema', value: systemEvents, color: 'hsl(var(--chart-4))' },
        ],
      };
    },
  });
};
